import type {
  LearningPath,
  Lesson,
  LessonKind,
  Module,
  SubsectionKind,
  Track,
} from '../evaluator/types.ts';
import { definirEconomia } from '../state/economia.ts';

export interface ManifestEntry {
  id: string;
  track: string;
  track_title: string;
  title: string;
  order_index: number;
  description: string;
  file: string;
  hash: string;
  lessons: string[];
  exercicios: number;
  /**
   * ADR 0027 — as subsecções e os seus tópicos, só com o que a página do
   * curso precisa para desenhar a lista e contar o progresso. Ausente em
   * manifestos anteriores a 05/09/2026 — normalizado para lista vazia ao
   * carregar, como os percursos.
   */
  subseccoes: SubseccaoDoManifesto[];
}

export interface TopicoDoManifesto {
  id: string;
  title: string;
  kind: LessonKind;
  exercicios: number;
}

export interface SubseccaoDoManifesto {
  id: string;
  title: string;
  kind: SubsectionKind;
  topicos: TopicoDoManifesto[];
}

/**
 * O catálogo: tudo o que o ecrã principal precisa, e nada do corpo das lições.
 *
 * A separação existe por causa do orçamento. Um cartão de módulo mostra título,
 * descrição e contagens — todos no manifesto. Os enunciados, os exercícios e as
 * soluções só se descarregam quando alguém entra no módulo.
 */
export interface Catalogo {
  trilhas: Track[];
  modulos: ManifestEntry[];
  /**
   * Percursos (ADR 0019): referências a módulos que já estão em `modulos`.
   * Ausente em manifestos anteriores a 16/08/2026 — normalizado para lista
   * vazia ao carregar, para nenhum ecrã ter de saber disso.
   */
  percursos: LearningPath[];
}

/**
 * O conteúdo não vive na base de dados (RF-011): é compilado a partir de Git e
 * servido como JSON estático. O nome do ficheiro inclui o resumo do conteúdo,
 * o que permite cache imutável — um pacote nunca muda debaixo de um estudante
 * que o tenha em cache; muda o nome.
 */
const BASE = '/content';

const cache = new Map<string, Module>();

export async function loadCatalogo(signal?: AbortSignal): Promise<Catalogo> {
  const response = await fetch(`${BASE}/manifest.json`, { signal });
  if (!response.ok) throw new Error(`manifest: ${response.status}`);
  const catalogo = (await response.json()) as Partial<Catalogo> & {
    economia?: { custo_dica?: number; bonus_licao?: number; peso_projecto?: number; factor_dica_desafio?: number };
  };
  // H-207 — a economia chega com o conteúdo e fica no aparelho: em modo de
  // avião vale o último valor visto, nunca zero.
  if (catalogo.economia) definirEconomia(catalogo.economia);
  return {
    // `nivel` chegou a 05/09/2026 (ADR 0015 decisão 5, via ADR 0027); um
    // manifesto anterior em cache não o tem, e o cartão não pode ficar a
    // mostrar uma chave de texto.
    trilhas: [...(catalogo.trilhas ?? [])]
      .map((t) => ({ ...t, nivel: t.nivel ?? 'iniciante' }))
      .sort((a, b) => a.order_index - b.order_index),
    modulos: [...(catalogo.modulos ?? [])]
      .map((m) => ({ ...m, subseccoes: m.subseccoes ?? [] }))
      .sort((a, b) => a.order_index - b.order_index),
    percursos: [...(catalogo.percursos ?? [])].sort((a, b) => a.order_index - b.order_index),
  };
}

export async function loadModule(entry: ManifestEntry, signal?: AbortSignal): Promise<Module> {
  const cached = cache.get(entry.file);
  if (cached) return cached;

  const response = await fetch(`${BASE}/${entry.file}`, { signal });
  if (!response.ok) throw new Error(`módulo ${entry.id}: ${response.status}`);
  const module = (await response.json()) as Module;
  cache.set(entry.file, module);
  return module;
}

/** Todos os módulos da trilha, por ordem. */
export async function loadModules(signal?: AbortSignal): Promise<Module[]> {
  const { modulos } = await loadCatalogo(signal);
  const modules = await Promise.all(modulos.map((entrada) => loadModule(entrada, signal)));
  return modules.sort((a, b) => a.order_index - b.order_index);
}

/**
 * A lição pedida **e todas as outras da trilha**. O contexto é preciso para
 * decidir se ela está desbloqueada (RF-014) e para dizer o que falta.
 *
 * São todas e não só as do módulo: a primeira lição de um módulo tem como
 * pré-requisito a última do anterior, e olhar só para dentro do módulo
 * deixaria essa lição bloqueada para sempre.
 */
export async function loadLessonContext(
  lessonId: string,
  signal?: AbortSignal,
): Promise<{ lesson: Lesson; todas: Lesson[]; trilha: string }> {
  const modules = await loadModules(signal);
  const todas = modules.flatMap((m) => m.lessons);
  const lesson = todas.find((l) => l.id === lessonId);
  if (!lesson) throw new Error(`lição desconhecida: ${lessonId}`);
  // A trilha vai junto para a saída da lição devolver à página do curso, com
  // a secção certa aberta (ADR 0027).
  const trilha = modules.find((m) => m.id === lesson.module)?.track ?? '';
  return { lesson, todas, trilha };
}

export async function loadLesson(lessonId: string, signal?: AbortSignal): Promise<Lesson> {
  return (await loadLessonContext(lessonId, signal)).lesson;
}
